import styled from "@emotion/styled"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material"
import { useState } from "react"
import {
  ArrowDown,
  CheckboxIcon,
  CheckedCheckboxIcon,
  Desktop,
  DocIcon,
  GetFileIcon,
  Mobile,
  NewText,
} from "ui"
import { extractProperty } from "utils"
import DownloadProgressDialog from "./DownloadProgressDialog"
import FileItem from "./FileItem"

type FileTypeAccordionProps = {
  fileType: string
  files: any[]
  messages: any
  onDownloadType: any
  onFileSelection: any
  isFileSelected: any
  createUniqueFileId: any
  downloadProgress: any

  totalFiles: any
}

const StyledWrapp = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const StyledRightSide = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`

const StyledLeftSide = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-right: 24px;
  gap: 24px;
`

const StyledButton = styled(Button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const StyledDetailsWrapper = styled.div``

const StyledSelectALlWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  label {
    margin: 0px;
  }
`

const FileTypeAccordion = ({
  fileType,
  files,
  messages,
  onDownloadType,
  onFileSelection,
  isFileSelected,
  createUniqueFileId,
  downloadProgress,

  totalFiles,
}: FileTypeAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [open, setOpen] = useState(false)

  // Check if all files in this category are selected
  const areAllFilesSelected = () => {
    if (!Array.isArray(files) || files.length === 0) return false
    return files.every((_, index) => {
      const uniqueId = createUniqueFileId(fileType, index)
      return isFileSelected(uniqueId)
    })
  }

  const handleSelectAllToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation() // Prevent accordion from toggling
    const shouldSelectAll = e.target.checked

    files.forEach((file, index) => {
      const uniqueId = createUniqueFileId(fileType, index)
      const isCurrentlySelected = isFileSelected(uniqueId)

      // Only trigger selection change if the state needs to change
      if (isCurrentlySelected !== shouldSelectAll) {
        onFileSelection(file, uniqueId, shouldSelectAll)
      }
    })
  }

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent accordion from toggling when clicking checkbox
  }

  const handleAccordionChange = (_: React.SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded)
  }

  // Get file name if there's only one file
  const getFileName = () => {
    if (files.length === 1) {
      return extractProperty(files[0], "fileName")
    }
    return undefined
  }

  return (
    <Accordion onChange={handleAccordionChange}>
      <AccordionSummary
        expandIcon={<ArrowDown />}
        aria-controls={`panel-${fileType}-content`}
        id={`panel-${fileType}-header`}
      >
        <StyledWrapp>
          <StyledRightSide>
            <DocIcon />
            <NewText component={"p"} desktopFontSize={16} color="#000000">
              <span style={{ fontWeight: 600, fontSize: 20 }}> {fileType} </span>
              <span style={{ display: "inline-block", whiteSpace: "nowrap" }}>
                ({Array.isArray(files) ? files.length : 0} {messages.documents})
              </span>
            </NewText>
          </StyledRightSide>
          <StyledLeftSide>
            <Desktop>
              {isExpanded && (
                <StyledSelectALlWrapper>
                  <FormControlLabel
                    control={
                      <Checkbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckedCheckboxIcon color="#2768A3" />}
                        checked={areAllFilesSelected()}
                        onChange={handleSelectAllToggle}
                        onClick={handleCheckboxClick}
                        title="Select all files in this category"
                      />
                    }
                    label={
                      <NewText
                        component={"p"}
                        desktopFontSize={18}
                        color="#000000"
                        style={{ textDecoration: "underline" }}
                        onClick={handleCheckboxClick}
                      >
                        {messages.selectAll}
                      </NewText>
                    }
                  />
                </StyledSelectALlWrapper>
              )}
            </Desktop>
            <StyledButton
              onClick={(e) => {
                e.stopPropagation() // Prevent accordion from toggling
                setOpen(true) // Open the download progress dialog
                onDownloadType(files)
              }}
            >
              <GetFileIcon />
              <NewText
                component={"p"}
                desktopFontSize={18}
                color="#000000"
                style={{ textDecoration: "underline", minWidth: "66px" }}
              >
                {messages.download}
              </NewText>
            </StyledButton>
          </StyledLeftSide>
        </StyledWrapp>
      </AccordionSummary>
      <Mobile>
        {isExpanded && (
          <StyledSelectALlWrapper>
            <Checkbox
              icon={<CheckboxIcon />}
              checkedIcon={<CheckedCheckboxIcon color="#2768A3" />}
              checked={areAllFilesSelected()}
              onChange={handleSelectAllToggle}
              onClick={handleCheckboxClick}
              title="Select all files in this category"
            />
            <NewText
              component={"p"}
              desktopFontSize={18}
              color="#000000"
              style={{ textDecoration: "underline" }}
            >
              {messages.selectAll}
            </NewText>
          </StyledSelectALlWrapper>
        )}
      </Mobile>
      <AccordionDetails>
        <StyledDetailsWrapper>
          {Array.isArray(files) &&
            files.map((file: any, index: number) => {
              const uniqueId = createUniqueFileId(fileType, index)
              const isSelected = isFileSelected(uniqueId)

              return (
                <FileItem
                  key={uniqueId}
                  file={file}
                  uniqueId={uniqueId}
                  isSelected={isSelected}
                  onSelectionChange={onFileSelection}
                />
              )
            })}
        </StyledDetailsWrapper>
      </AccordionDetails>
      {/* Download progress dialog */}
      <DownloadProgressDialog
        open={open}
        onClose={() => setOpen(false)}
        downloadProgress={downloadProgress}
        selectedCount={files.length}
        totalFiles={totalFiles}
        messages={messages}
        fileCategory={fileType}
        fileName={getFileName()}
      />
    </Accordion>
  )
}

export default FileTypeAccordion
